import { head, size } from "lodash";
import { type FormEventHandler, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { getProducts } from "@/api";
import { useCurrentStore } from "@/hooks";
import Button from "../button";
import { InputText } from "../input";

type ProductSearchFormProps = {
	onProductSelect: (product: Product) => void;
	productSearchInputRef?: React.RefObject<HTMLInputElement | null>;
};

/** Product search form
 * Used to search and select a product by SKU, UPC, or Manufacturer SKU.
 * The component uses a form because scanners typically emulate keyboard input followed by an Enter key.
 */
const ProductSearchForm = ({
	onProductSelect,
	productSearchInputRef,
}: ProductSearchFormProps) => {
	const store = useCurrentStore();
	const { t } = useTranslation();

	const onSubmit = useCallback<FormEventHandler<HTMLFormElement>>(
		async (e) => {
			e.preventDefault();
			try {
				const formData = new FormData(e.currentTarget);
				const query = formData.get("query");
				const { data } = await getProducts({
					"q[storeIdEq]": store?.id,
					"q[skuOrUpcOrManufacturerSkuEq]": query,
					"q[archivedAtNull]": true,
				});

				if (size(data) === 1) {
					onProductSelect(head(data) as Product);
					(e.target as HTMLFormElement).reset();
				}
			} catch (err) {
				console.error(err);
			}
		},
		[onProductSelect, store],
	);

	return (
		<form className="flex flex-1 flex-row gap-4" onSubmit={onSubmit}>
			<InputText
				ref={productSearchInputRef}
				name="query"
				placeholder={t("checkout.searchPlaceholder")}
			/>
			<Button variant="outline" type="submit">
				{t("checkout.searchButton")}
			</Button>
		</form>
	);
};

export default ProductSearchForm;
